import { z } from 'zod';

export const carFormSchema = z.object({
  brandId: z.number().min(1, 'Please select a brand'),
  modelId: z.number().min(1, 'Please select a model'),
  year: z.number().min(1900, 'Year must be at least 1900').max(new Date().getFullYear() + 1, 'Year cannot be in the future'),
  color: z.string().min(1, 'Please enter a color'),
  note: z.string().max(200, 'Note must be less than 200 characters').optional(),
  salePrice: z.union([z.string(), z.number()]).transform((val) => {
    if (val === '' || val === undefined) return 0;
    return typeof val === 'string' ? parseFloat(val) || 0 : val;
  }).refine((val) => val > 0, 'Sale price must be greater than 0'),
  deposit: z.union([z.string(), z.number()]).transform((val) => {
    if (val === '' || val === undefined) return 0;
    return typeof val === 'string' ? parseFloat(val) || 0 : val;
  }).refine((val) => val >= 0, 'Deposit must be 0 or greater'),
  
  // Seller details
  sellerName: z.string().min(1, 'Seller name is required').max(100, 'Seller name must be less than 100 characters'),
  sellerIdNo: z.string().min(1, 'Seller ID number is required').max(50, 'Seller ID number must be less than 50 characters'),
  sellerAddress: z.string().max(200, 'Seller address must be less than 200 characters').optional(),
  sellerContact: z.string().min(1, 'Seller contact is required').max(20, 'Seller contact must be less than 20 characters'),
  
  // Buyer details
  buyerName: z.string().min(1, 'Buyer name is required').max(100, 'Buyer name must be less than 100 characters'),
  buyerIdNo: z.string().min(1, 'Buyer ID number is required').max(50, 'Buyer ID number must be less than 50 characters'),
  buyerAddress: z.string().max(200, 'Buyer address must be less than 200 characters').optional(),
  buyerContact: z.string().min(1, 'Buyer contact is required').max(20, 'Buyer contact must be less than 20 characters'),
  
  // Chassis number (optional)
  chassisNo: z.string().max(50, 'Chassis number must be less than 50 characters').optional(),
  
  // Plate number (optional)
  plateNumber: z.string().max(20, 'Plate number must be less than 20 characters').optional(),
  
  // Registered owner (optional)
  registeredOwner: z.string().max(100, 'Registered owner must be less than 100 characters').optional(),
}).refine((data) => data.deposit <= data.salePrice, {
  message: 'Deposit cannot be greater than sale price',
  path: ['deposit'],
});

export const createPaymentFormSchema = (maxAmount: number) => z.object({
  amount: z.number()
    .min(0.01, 'Payment amount must be greater than 0')
    .max(maxAmount, `Payment amount cannot exceed remaining balance of ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(maxAmount)}`),
  notes: z.string().max(200, 'Notes must be less than 200 characters').optional(),
});

export type CarFormData = {
  brandId: number;
  modelId: number;
  year: number;
  color: string;
  note?: string;
  salePrice: string | number;
  deposit: string | number;
  sellerName: string;
  sellerIdNo: string;
  sellerAddress?: string;
  sellerContact: string;
  buyerName: string;
  buyerIdNo: string;
  buyerAddress?: string;
  buyerContact: string;
  chassisNo?: string;
  plateNumber?: string;
  registeredOwner?: string;
};

// Add car to warehouse
export const warehouseCarFormSchema = z.object({
  brandId: z.number().min(1, 'Please select a brand'),
  modelId: z.number().min(1, 'Please select a model'),
  year: z.number().min(1900).max(new Date().getFullYear() + 1),
  color: z.string().min(1, 'Please enter a color'),
  chassisNo: z.string().max(50).optional(),
  plateNumber: z.string().max(20).optional(),
  engineNo: z.string().max(50).optional(),
  mileage: z.union([z.string(), z.number()]).transform((v) => (v === '' || v == null ? undefined : typeof v === 'string' ? parseInt(v, 10) : v)).optional(),
  purchasePrice: z.union([z.string(), z.number()]).transform((v) => (v === '' || v == null ? 0 : typeof v === 'string' ? parseFloat(v) : v)).refine((v) => v >= 0, 'Must be 0 or greater'),
  purchaseCurrency: z.enum(['IQD', 'USD']).optional(),
  sellerId: z.union([z.string(), z.number()]).optional(),
  notes: z.string().max(500).optional(),
  registeredOwner: z.string().max(100).optional(),
});
export type WarehouseCarFormData = z.infer<typeof warehouseCarFormSchema>;

// Sell from warehouse (sale form) – supports "from warehouse" or "direct sell" (add car then sell)
export const saleFormSchema = z.object({
  sellMode: z.enum(['warehouse', 'direct']),
  warehouseCarId: z.number().optional(),
  // Direct sell car fields (when sellMode === 'direct')
  brandId: z.number().optional(),
  modelId: z.number().optional(),
  carYear: z.number().optional(),
  carColor: z.string().optional(),
  carPlateNo: z.string().optional(),
  carChassisNo: z.string().optional(),
  carRegisteredOwner: z.string().optional(),
  buyerId: z.union([z.number(), z.string()]).optional(),
  salePrice: z.union([z.string(), z.number()]).transform((v) => (v === '' || v == null ? 0 : typeof v === 'string' ? parseFloat(v) : v)).refine((v) => v > 0, 'Sale price must be greater than 0'),
  deposit: z.union([z.string(), z.number()]).transform((v) => (v === '' || v == null ? 0 : typeof v === 'string' ? parseFloat(v) : v)).refine((v) => v >= 0, 'Deposit must be 0 or greater'),
  currency: z.enum(['IQD', 'USD']).optional(),
  sellerName: z.string().optional(),
  sellerIdNo: z.string().optional(),
  sellerAddress: z.string().optional(),
  sellerContact: z.string().optional(),
  buyerName: z.string().optional(),
  buyerIdNo: z.string().optional(),
  buyerAddress: z.string().optional(),
  buyerContact: z.string().optional(),
  notes: z.string().max(500).optional(),
})
  .refine((d) => d.deposit <= d.salePrice, { message: 'Deposit cannot exceed sale price', path: ['deposit'] })
  .refine((d) => d.sellMode !== 'warehouse' || (d.warehouseCarId != null && d.warehouseCarId >= 1), { message: 'Please select a car from warehouse', path: ['warehouseCarId'] })
  .refine((d) => d.sellMode !== 'direct' || (d.brandId != null && d.brandId >= 1 && d.modelId != null && d.modelId >= 1 && d.carYear != null && (d.carColor ?? '').trim() !== ''), { message: 'Please fill car details (brand, model, year, color)', path: ['brandId'] });
export type SaleFormData = z.infer<typeof saleFormSchema>;

export type PaymentFormData = z.infer<ReturnType<typeof createPaymentFormSchema>>;
